import React, { useState } from 'react';
import ArtistForm from './components/ArtistForm';
import ArtistSortPopup from './components/ArtistSortPopup';
import ArtistFilterPopup from './components/ArtistFilterPopup';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import en from '../../translations/en.json';
import { CREATE_ARTIST_URL, GET_ARTISTS_URL, CHECK_ARTIST_NAME_URL } from '../../api/artist';
import { apiClient } from '../../api/apiClient';
import { toast } from '../../components/shared/toast';
import EmptyState from '../../components/shared/EmptyState';
import './Artists.css';

function Artists({ onSelectArtist }) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateArtists = canCreate('artists');
  const canEditArtists = canEdit('artists');
  const canDeleteArtists = canDelete('artists');

  const [artists, setArtists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingArtist, setIsAddingArtist] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState('Songs A-Z');
  const [filters, setFilters] = useState({
    role: null,
    gender: null,
    tracks: null
  });
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingArtistData, setPendingArtistData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalArtists, setTotalArtists] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const artistsPerPage = 12;

  React.useEffect(() => {
    const fetchArtists = async () => {
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page: currentPage,
          limit: artistsPerPage,
          search: searchQuery,
          role: filters.role || '',
          gender: filters.gender || '',
          sort: selectedSort
        }).toString();

        const data = await apiClient(`${GET_ARTISTS_URL}?${queryParams}`);
        if (data) {
          setArtists(data.artists || []);
          setTotalArtists(data.totalCount || 0);
        }
      } catch (err) {
        console.warn('Failed to load artists from backend:', err);
        setArtists([]);
        setTotalArtists(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchArtists();
  }, [currentPage, searchQuery, filters.role, filters.gender, selectedSort]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      role: null,
      gender: null,
      tracks: null
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const toggleBookmark = (id, e) => {
    e.stopPropagation();
    setBookmarkedIds((prev) => 
      prev.includes(id) ? prev.filter(bId => bId !== id) : [...prev, id]
    );
  };

  // Pagination & Filter Calculations
  const totalPages = Math.ceil(totalArtists / artistsPerPage) || 1;
  const activePage = Math.max(1, Math.min(currentPage, totalPages));
  
  const startIndex = (activePage - 1) * artistsPerPage;
  const endIndex = Math.min(startIndex + artistsPerPage, totalArtists);
  
  const paginatedArtists = artists;

  const downloadCSV = (artistsList) => {
    const headers = ['Artist Code', 'Name', 'Gender', 'Roles', 'Status', 'Songs Count'];
    const rows = artistsList.map(a => [
      a.code || a.artist_code || '',
      a.name || '',
      a.gender === 'F' || a.gender === 'Female' ? 'Female' : (a.gender === 'O' || a.gender === 'Other/Band' || a.gender === 'Other/Group' ? 'Other/Group' : 'Male'),
      a.types ? a.types.join(', ') : '',
      a.status ? 'Active' : 'Inactive',
      a.songsCount || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `artists_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const queryParams = new URLSearchParams({
        search: searchQuery,
        role: filters.role || '',
        gender: filters.gender || '',
        sort: selectedSort,
        export: 'true'
      }).toString();

      const data = await apiClient(`${GET_ARTISTS_URL}?${queryParams}`);
      if (data && data.artists) {
        downloadCSV(data.artists);
        toast.success('Data exported successfully!');
      } else {
        toast.error('Failed to export data.');
      }
    } catch (err) {
      console.error('Export Error:', err);
      toast.error('Error exporting data.');
    } finally {
      setIsExporting(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (activePage > 4) {
        pages.push('...');
      }
      const start = Math.max(2, activePage - 2);
      const end = Math.min(totalPages - 1, activePage + 2);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (activePage < totalPages - 3) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const proceedSaveArtist = async (newArtistData) => {
    try {
      // Assemble FormData locally in the component as requested
      const formData = new FormData();
      formData.append('name', newArtistData.artistName);
      formData.append('gender', newArtistData.gender);
      formData.append('status', String(newArtistData.status));
      
      if (Array.isArray(newArtistData.artistTypes)) {
        newArtistData.artistTypes.forEach((type) => {
          formData.append('types', type);
        });
      }

      if (newArtistData.artistImage && newArtistData.artistImage.file) {
        formData.append('image', newArtistData.artistImage.file);
      }

      const savedArtist = await apiClient(CREATE_ARTIST_URL, {
        method: 'POST',
        body: formData,
      });
      
      const newArtistObj = {
        id: savedArtist.id || (artists.length + 1),
        name: savedArtist.name || newArtistData.artistName,
        code: savedArtist.code || `ART-${Math.floor(100000 + Math.random() * 900000)}`,
        gender: savedArtist.gender || newArtistData.gender,
        types: savedArtist.types || newArtistData.artistTypes,
        status: savedArtist.status || 'Active',
        songsCount: savedArtist.songsCount || 0,
        avatar: savedArtist.imageUrl || newArtistData.artistImage?.previewUrl || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=200&h=200'
      };

      setArtists([newArtistObj, ...artists]);
      setIsAddingArtist(false);
      toast.success('Artist saved successfully!');
    } catch (err) {
      console.warn('API call failed, falling back to mock save:', err);
      
      const mockCode = `ART-${Math.floor(100000 + Math.random() * 900000)}`;
      const newArtistObj = {
        id: artists.length + 1,
        name: capitalize(newArtistData.artistName),
        code: mockCode,
        gender: newArtistData.gender,
        types: newArtistData.artistTypes,
        status: 'Active',
        songsCount: 0,
        avatar: newArtistData.artistImage?.previewUrl || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=200&h=200'
      };

      setArtists([newArtistObj, ...artists]);
      setIsAddingArtist(false);
      toast.success('Artist saved successfully (Mock)!');
    }
  };

  const handleSaveArtist = async (newArtistData) => {
    try {
      const checkResult = await apiClient(CHECK_ARTIST_NAME_URL, {
        method: 'POST',
        body: { name: newArtistData.artistName }
      });
      if (checkResult.exists) {
        setPendingArtistData(newArtistData);
        setShowConfirmModal(true);
        return;
      }
    } catch (err) {
      console.warn('Backend name duplicate check failed:', err);
    }
    await proceedSaveArtist(newArtistData);
  };

  const handleConfirmDuplicate = async () => {
    setShowConfirmModal(false);
    if (pendingArtistData) {
      await proceedSaveArtist(pendingArtistData);
      setPendingArtistData(null);
    }
  };

  const handleCancelDuplicate = () => {
    setShowConfirmModal(false);
    setPendingArtistData(null);
  };

  const capitalize = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (isAddingArtist) {
    return (
      <>
        <ArtistForm 
          isModal={false}
          onCancel={() => setIsAddingArtist(false)}
          onSave={handleSaveArtist}
        />
        <ConfirmationModal
          isOpen={showConfirmModal}
          title={t('artists.confirmDuplicateTitle')}
          message={t('artists.confirmDuplicateMessage')}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          onConfirm={handleConfirmDuplicate}
          onCancel={handleCancelDuplicate}
        />
      </>
    );
  }

  const resultCount = totalArtists;

    return (
      <ProtectedPage module="artists">
        <div className="artists-page-wrapper">
        {/* Shared Header Controls */}
        <SharedHeader
          title={t('artists.title')}
          searchQuery={searchQuery}
          onSearchChange={(val) => {
            setSearchQuery(val);
            setCurrentPage(1);
          }}
          searchPlaceholder={t('artists.searchPlaceholder')}
          resultCount={resultCount}
          resultLabel={t('artists.resultsCount')}
          onSortClick={() => {
            setSortOpen(!sortOpen);
            setFilterOpen(false);
          }}
          onFilterClick={() => {
            setFilterOpen(!filterOpen);
            setSortOpen(false);
          }}
          sortActive={sortOpen}
          filterActive={filterOpen}
          isSortApplied={selectedSort !== 'Songs A-Z'}
          isFilterApplied={filters.role !== null || filters.gender !== null || filters.tracks !== null}
          onExportClick={handleExport}
          onAddNewClick={canCreateArtists ? () => setIsAddingArtist(true) : null}
          addNewText={t('artists.addNew')}
          canAddNew={canCreateArtists}
        />

      {/* Popups anchor wrapper */}
      <div className="popup-anchor-container">
        {sortOpen && (
          <ArtistSortPopup
            selectedSort={selectedSort}
            onSelect={setSelectedSort}
            onClose={() => setSortOpen(false)}
            t={t}
          />
        )}
        {filterOpen && (
          <ArtistFilterPopup
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onClose={() => setFilterOpen(false)}
          />
        )}
      </div>

        {/* Artists grid card listing */}
        <div className="artists-cards-listing-grid">
          {isLoading ? (
            <LoadingOverlay isLoading message="Loading artists, please wait." />
          ) : paginatedArtists.length > 0 ? (
            paginatedArtists.map((artist) => (
            <div 
              key={artist.id} 
              className="artist-listing-card"
              onClick={() => onSelectArtist?.(artist)}
              style={{ cursor: 'pointer' }}
            >
              {/* Card top cover artwork */}
              <div className="listing-card-artwork-box">
                <img src={artist.avatar} alt={artist.name} className="listing-card-avatar" />
                {(canEditArtists || canDeleteArtists) && (
                  <button 
                    type="button" 
                    className="listing-card-dots-btn" 
                    aria-label="Artist options"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Body elements */}
              <div className="listing-card-info-body">
                <h3 className="listing-card-name-title">{artist.name}</h3>
                
                {/* Role tags row */}
                <div className="listing-card-roles-row">
                  {artist.types.map((type) => (
                    <span key={type} className={`role-pill-badge pill-${type.toLowerCase()}`}>
                      {type}
                    </span>
                  ))}
                </div>

                {/* Song Counter metric box */}
                <div className="listing-card-counter-box">
                  <div className="counter-icon-wrapper">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                  </div>
                  <div className="counter-details">
                    <span className="counter-number">{artist.songsCount || 0}</span>
                    <span className="counter-label">{t('artists.songsCount')}</span>
                  </div>
                </div>
              </div>

              {/* Action and bookmark footer row */}
              <div className="listing-card-footer-row">
                <button 
                  type="button" 
                  className="view-profile-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectArtist?.(artist);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <span>{t('artists.viewProfile')}</span>
                </button>

                <button 
                  type="button" 
                  className={`bookmark-action-btn ${bookmarkedIds.includes(artist.id) ? 'bookmarked' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBookmark(artist.id, e);
                  }}
                  aria-label="Bookmark artist"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={bookmarkedIds.includes(artist.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                  </svg>
                </button>
              </div>
            </div>
          ))
        ) : (
          <EmptyState
            hasSearch={!!(searchQuery || filters.role || filters.gender || filters.tracks)}
            title={!!(searchQuery || filters.role || filters.gender || filters.tracks) ? 'No artists found' : 'No artists yet'}
            subtitle={!!(searchQuery || filters.role || filters.gender || filters.tracks)
              ? 'Try adjusting your search, sort, or filter criteria.'
              : 'Add your first artist to get started.'}
          />
        )}
      </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="artists-pagination-row">
            <span className="pagination-range-text">
              {totalArtists > 0 ? `${startIndex + 1}-${endIndex} of ${totalArtists}` : '0-0 of 0'}
            </span>

            <div className="pagination-controls-wrapper">
              <button 
                type="button" 
                className="pager-btn outlined"
                disabled={activePage === 1}
                onClick={() => setCurrentPage(1)}
                style={activePage === 1 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                &lt;&lt;
              </button>
              <button 
                type="button" 
                className="pager-btn outlined"
                disabled={activePage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                style={activePage === 1 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                &lt;
              </button>
              
              {getPageNumbers().map((pageNum, idx) => {
                if (pageNum === '...') {
                  return <span key={`sep-${idx}`} className="pager-separator">...</span>;
                }
                return (
                  <button
                    key={`page-${pageNum}`}
                    type="button"
                    className={`pager-btn ${activePage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button 
                type="button" 
                className="pager-btn outlined"
                disabled={activePage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                style={activePage === totalPages ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                &gt;
              </button>
              <button 
                type="button" 
                className="pager-btn outlined"
                disabled={activePage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
                style={activePage === totalPages ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                &gt;&gt;
              </button>
            </div>
          </div>
        )}
        <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage')} />
        </div>
      </ProtectedPage>
    );
  }

export default Artists;
